import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FaArrowLeft, FaShieldAlt, FaLock, FaUserSecret,
  FaCookieBite, FaDatabase, FaEnvelope, FaUserShield,
  FaInfoCircle, FaCheck
} from 'react-icons/fa';
import './LegalPage.css';

const Privacy = () => {
  const lastUpdated = 'September 22, 2026';

  return (
    <div className="legal-page">
      <div className="legal-container">
        <Link to="/" className="back-link">
          <FaArrowLeft /> Back to Home
        </Link>

        {/* Header */}
        <div className="legal-header">
          <div className="header-icon-wrap blue">
            <FaShieldAlt className="header-icon" />
          </div>
          <h1>Privacy Policy</h1>
          <p className="legal-subtitle">
            Last updated: <strong>{lastUpdated}</strong>
          </p>
          <p className="legal-intro">
            Your privacy is important to us. This Privacy Policy explains 
            what information we collect, how we use it, and the choices you 
            have regarding your data.
          </p>
        </div>

        {/* Quick Summary */}
        <div className="legal-summary">
          <h3><FaInfoCircle /> Summary</h3>
          <div className="summary-grid">
            <div className="summary-item">
              <FaLock className="summary-icon green" />
              <div>
                <strong>We Protect Your Data</strong>
                <p>Bank-level encryption for all sensitive information</p>
              </div>
            </div>
            <div className="summary-item">
              <FaUserSecret className="summary-icon blue" />
              <div>
                <strong>We Don't Sell Your Data</strong>
                <p>Your information is never sold to third parties</p>
              </div>
            </div>
            <div className="summary-item">
              <FaCookieBite className="summary-icon gold" />
              <div>
                <strong>Minimal Cookies</strong>
                <p>Only essential cookies for functionality</p>
              </div>
            </div>
            <div className="summary-item">
              <FaUserShield className="summary-icon purple" />
              <div>
                <strong>You Control Your Data</strong>
                <p>Request access, correction, or deletion anytime</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="legal-content">
          <section className="legal-section">
            <h2><span className="section-number">1</span> Information We Collect</h2>
            
            <h3>Information you provide directly:</h3>
            <ul>
              <li>Username (extracted from your email)</li>
              <li>Email address</li>
              <li>Password (securely hashed, never stored in plain text)</li>
              <li>Selected currency preference</li>
              <li>Bank account details (for withdrawals)</li>
              <li>X (Twitter) account information (optional, if you connect)</li>
            </ul>

            <h3>Information collected automatically:</h3>
            <ul>
              <li>IP address and device information</li>
              <li>Browser type and version</li>
              <li>Pages visited and time spent on the platform</li>
              <li>Referral source (if applicable)</li>
              <li>Login timestamps and activity logs</li>
            </ul>

            <h3>Financial information:</h3>
            <ul>
              <li>Transaction history (deposits, withdrawals, winnings)</li>
              <li>Payment method tokens (we do NOT store card numbers)</li>
              <li>Crypto wallet addresses (for deposits)</li>
              <li>Transaction IDs for verification</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">2</span> How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul>
              <li><strong>Operate the platform:</strong> Process deposits, withdrawals, and predictions</li>
              <li><strong>Verify identity:</strong> Prevent fraud and ensure account security</li>
              <li><strong>Communicate:</strong> Send transaction confirmations, notifications, and updates</li>
              <li><strong>Improve services:</strong> Analyze usage patterns to enhance features</li>
              <li><strong>Comply with laws:</strong> Meet legal and regulatory requirements</li>
              <li><strong>Prevent abuse:</strong> Detect and prevent fraud, cheating, and malicious activity</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">3</span> Information Sharing</h2>
            <p>
              We <strong>do NOT sell</strong> your personal information. 
              We only share data with:
            </p>

            <div className="sharing-grid">
              <div className="sharing-card">
                <FaLock className="sharing-icon blue" />
                <h4>Payment Processors</h4>
                <p>
                  Flutterwave (for fiat payments) receives necessary payment 
                  details to process your transactions.
                </p>
              </div>
              <div className="sharing-card">
                <FaDatabase className="sharing-icon purple" />
                <h4>Service Providers</h4>
                <p>
                  Cloud hosting, analytics, and email providers that help 
                  operate the platform.
                </p>
              </div>
              <div className="sharing-card">
                <FaShieldAlt className="sharing-icon red" />
                <h4>Legal Authorities</h4>
                <p>
                  When required by law, court order, or to protect our 
                  legal rights.
                </p>
              </div>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">4</span> Data Security</h2>
            <p>We protect your data with:</p>
            <ul>
              <li><strong>HTTPS encryption</strong> for all data in transit</li>
              <li><strong>Password hashing</strong> using industry-standard algorithms</li>
              <li><strong>JWT tokens</strong> for secure authentication</li>
              <li><strong>Regular security audits</strong> and updates</li>
              <li><strong>Restricted access</strong> to personal data on a need-to-know basis</li>
              <li><strong>Monitoring</strong> for suspicious activity</li>
            </ul>
            <p className="legal-highlight">
              While we use reasonable security measures, no system is 100% 
              secure. Please use a strong, unique password.
            </p>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">5</span> Cookies & Tracking</h2>
            <p>We use minimal cookies for:</p>
            <ul>
              <li><strong>Authentication:</strong> Keeping you logged in</li>
              <li><strong>Preferences:</strong> Remembering your currency and settings</li>
              <li><strong>Security:</strong> Detecting suspicious activity</li>
            </ul>
            <p>
              You can disable cookies in your browser, but this may affect 
              platform functionality.
            </p>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">6</span> Your Rights</h2>
            <p>You have the right to:</p>
            <div className="rights-grid">
              <div className="right-item">
                <FaCheck className="right-icon" />
                <div>
                  <strong>Access</strong>
                  <p>Request a copy of your personal data</p>
                </div>
              </div>
              <div className="right-item">
                <FaCheck className="right-icon" />
                <div>
                  <strong>Correction</strong>
                  <p>Update inaccurate or incomplete data</p>
                </div>
              </div>
              <div className="right-item">
                <FaCheck className="right-icon" />
                <div>
                  <strong>Deletion</strong>
                  <p>Request deletion of your account and data</p>
                </div>
              </div>
              <div className="right-item">
                <FaCheck className="right-icon" />
                <div>
                  <strong>Portability</strong>
                  <p>Export your data in a readable format</p>
                </div>
              </div>
              <div className="right-item">
                <FaCheck className="right-icon" />
                <div>
                  <strong>Objection</strong>
                  <p>Object to specific data processing</p>
                </div>
              </div>
              <div className="right-item">
                <FaCheck className="right-icon" />
                <div>
                  <strong>Withdraw Consent</strong>
                  <p>Opt-out of non-essential communications</p>
                </div>
              </div>
            </div>
            <p>
              To exercise these rights, email us at{' '}
              <strong>privacy@xpunters.com</strong>. We will respond within 
              30 days.
            </p>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">7</span> Data Retention</h2>
            <p>We retain your data:</p>
            <ul>
              <li><strong>Active accounts:</strong> While your account is active</li>
              <li><strong>Closed accounts:</strong> Up to 5 years for legal and tax requirements</li>
              <li><strong>Transaction records:</strong> Up to 7 years as required by financial regulations</li>
              <li><strong>Marketing data:</strong> Until you opt out</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">8</span> Children's Privacy</h2>
            <p>
              XPunters is not intended for users under 18. We do not knowingly 
              collect data from children. If we discover an underage user, 
              their account will be terminated and their data deleted.
            </p>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">9</span> International Transfers</h2>
            <p>
              Your data may be transferred to and processed in countries 
              outside your own. We ensure appropriate safeguards are in place 
              for such transfers.
            </p>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">10</span> Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Significant 
              changes will be communicated via email or platform notification. 
              The "Last updated" date at the top reflects the most recent 
              revision.
            </p>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">11</span> Contact Us</h2>
            <p>For privacy-related questions or concerns:</p>
            <div className="contact-block">
              <p><strong>Email:</strong> privacy@xpunters.com</p>
              <p><strong>Data Protection Officer:</strong> dpo@xpunters.com</p>
              <p><strong>Response Time:</strong> Within 30 days</p>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="legal-footer">
          <p>
            Your trust is important to us. We are committed to protecting 
            your privacy and being transparent about our practices.
          </p>
          <div className="legal-footer-actions">
            <Link to="/terms" className="btn btn-secondary">
              View Terms of Service
            </Link>
            <Link to="/contact" className="btn btn-primary">
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;